import { Component } from 'react';

/**
 * Catches render errors in the tree below it.
 *
 * Without this, a single unexpected error in one section blanks the entire
 * page -- including the contact details a visitor would use to reach the firm
 * instead. The fallback therefore keeps a plain route out of the site and
 * names the direct email address.
 *
 * React error boundaries must be class components; there is no hook
 * equivalent. Errors are logged rather than swallowed, so a real failure is
 * still visible in the browser console.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Console only: the site ships no error-reporting service, and inventing
    // one here would mean sending a visitor's data somewhere nobody has
    // agreed to.
    // eslint-disable-next-line no-console
    console.error('Unexpected application error', error, info);
  }

  render() {
    const { children } = this.props;

    if (this.state.hasError) {
      return (
        <div className="error-fallback">
          <div className="container container--narrow">
            <h1 className="error-fallback__title">Something went wrong</h1>
            <p className="error-fallback__body">
              Part of this page could not be displayed. Please reload the page,
              or email us at{' '}
              <a className="text-link" href="mailto:info@qaaslab.com">
                info@qaaslab.com
              </a>{' '}
              and we will help directly.
            </p>
            <button
              type="button"
              className="btn btn--primary btn--md"
              onClick={() => window.location.reload()}
            >
              Reload the page
            </button>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
